<template>
  <div class="battle-unit-dial" :class="{ open }">
    <Teleport to="body">
      <div
        v-if="open"
        class="wheel-overlay"
        @pointerdown.stop
        @touchstart.stop
        @touchmove.stop.prevent
      >
        <div class="wheel-backdrop" @click.self="close"></div>

        <button
          type="button"
          class="wheel-close"
          aria-label="Close"
          @click="close"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>

        <div class="wheel-stage" role="dialog" aria-label="Select deploy unit">
          <div class="wheel">
            <svg class="wheel-svg" viewBox="0 0 100 100" aria-hidden="true">
              <circle class="wheel-rim" cx="50" cy="50" r="48.6" />
              <path
                v-for="slice in slices"
                :key="slice.key"
                class="wheel-slice"
                :class="{ active: slice.active, disabled: !slice.selectable }"
                :d="slice.path"
                :fill-rule="slice.fillRule"
                @click="onPick(slice.item)"
              />
              <circle class="wheel-inner-ring" cx="50" cy="50" r="18" />
            </svg>

            <button
              v-for="slice in slices"
              :key="`${slice.key}-icon`"
              type="button"
              class="slice-icon"
              :class="{ active: slice.active, disabled: !slice.selectable, hero: slice.item.kind === 'hero' }"
              :style="slice.iconStyle"
              :disabled="!slice.item.selectable"
              :aria-label="slice.item.name"
              @click="onPick(slice.item)"
            >
              <img
                v-if="slice.item.kind === 'hero' && slice.item.image"
                class="slice-portrait"
                :src="slice.item.image"
                alt=""
              />
              <i
                v-else-if="slice.item.kind === 'hero'"
                class="fas fa-user-shield"
                aria-hidden="true"
              ></i>
              <i
                v-else
                :class="slice.item.icon"
                :style="{ color: slice.item.color }"
                aria-hidden="true"
              ></i>
              <span v-if="slice.item.kind === 'unit'" class="slice-count">
                {{ slice.item.remaining }}
              </span>
            </button>
          </div>

          <div class="wheel-panel">
            <p class="panel-kicker">{{ panelKicker }}</p>
            <h2 class="panel-name">{{ hubName }}</h2>
            <div v-if="showStepper" class="panel-stepper">
              <button
                type="button"
                class="step-btn"
                :disabled="placeQty <= 1 || sliderMax < 1"
                aria-label="Decrease quantity"
                @click="$emit('set-qty', placeQty - 1)"
              >
                −
              </button>
              <span class="step-value">{{ placeQty }}</span>
              <button
                type="button"
                class="step-btn"
                :disabled="placeQty >= sliderMax || sliderMax < 1"
                aria-label="Increase quantity"
                @click="$emit('set-qty', placeQty + 1)"
              >
                +
              </button>
              <button
                type="button"
                class="step-max"
                :disabled="sliderMax < 1"
                @click="$emit('set-qty', sliderMax)"
              >
                MAX
              </button>
            </div>
            <button type="button" class="panel-confirm" @click="close">
              Confirm &amp; Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <button
      type="button"
      class="dial-pill"
      :class="{ active: open }"
      @pointerdown.stop
      @touchstart.stop
      @click="toggle"
    >
      <span class="pill-icon" :style="pillColorStyle">
        <img
          v-if="selectedHero?.image"
          class="pill-portrait"
          :src="selectedHero.image"
          alt=""
        />
        <i v-else-if="selectedHero" class="fas fa-user-shield" aria-hidden="true"></i>
        <i v-else-if="selectedUnit" :class="selectedUnit.icon" aria-hidden="true"></i>
        <i v-else class="fas fa-shield-alt" aria-hidden="true"></i>
      </span>
      <span class="pill-copy">
        <template v-if="selectedHero">{{ selectedHero.name }}</template>
        <template v-else-if="selectedUnit">
          {{ selectedUnit.name }}
          <span class="pill-batch">Batch: {{ placeQty }}</span>
        </template>
        <template v-else>Select unit</template>
      </span>
      <span class="pill-caret" aria-hidden="true">▾</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";

const CX = 50;
const CY = 50;
const INNER_R = 18;
const OUTER_R = 48.4;
const ICON_R = 34;

const props = defineProps({
  open: { type: Boolean, default: false },
  heroes: { type: Array, default: () => [] },
  units: { type: Array, default: () => [] },
  selectedSlug: { type: String, default: null },
  selectedHeroId: { type: Number, default: null },
  placeQty: { type: Number, default: 1 },
  sliderMax: { type: Number, default: 0 },
});

const emit = defineEmits(["update:open", "select-hero", "select-unit", "set-qty"]);

const showStepper = computed(
  () => !!props.selectedSlug && !props.selectedHeroId
);

const selectedHero = computed(
  () =>
    props.heroes.find(
      (hero) => Number(hero.id) === Number(props.selectedHeroId)
    ) || null
);

const selectedUnit = computed(
  () => props.units.find((row) => row.slug === props.selectedSlug) || null
);

const hubName = computed(() => {
  if (selectedHero.value) return selectedHero.value.name;
  if (selectedUnit.value) return selectedUnit.value.name;
  return "Choose a unit";
});

const panelKicker = computed(() => {
  if (showStepper.value) return `Batch · ${props.placeQty}`;
  if (selectedHero.value) return "Hero";
  return "Select";
});

const pillColorStyle = computed(() => {
  if (selectedUnit.value?.color) return { color: selectedUnit.value.color };
  return undefined;
});

const fanItems = computed(() => {
  const heroes = (props.heroes || []).map((hero) => ({
    key: `hero-${hero.id}`,
    kind: "hero",
    id: hero.id,
    name: hero.name,
    image: hero.image || "",
    selectable: !!hero.selectable,
    active: Number(props.selectedHeroId) === Number(hero.id),
    hero,
  }));
  const units = (props.units || []).map((row) => ({
    key: `unit-${row.slug}`,
    kind: "unit",
    slug: row.slug,
    name: row.name,
    icon: row.icon,
    color: row.color,
    remaining: row.remaining,
    selectable: !!row.selectable,
    active: row.slug === props.selectedSlug,
    row,
  }));
  return [...heroes, ...units];
});

function polar(radius, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(rad),
    y: CY + radius * Math.sin(rad),
  };
}

function slicePath(index, count) {
  if (count < 1) return "";
  if (count === 1) {
    return [
      `M ${CX} ${CY - OUTER_R}`,
      `A ${OUTER_R} ${OUTER_R} 0 1 1 ${CX} ${CY + OUTER_R}`,
      `A ${OUTER_R} ${OUTER_R} 0 1 1 ${CX} ${CY - OUTER_R}`,
      `M ${CX} ${CY - INNER_R}`,
      `A ${INNER_R} ${INNER_R} 0 1 0 ${CX} ${CY + INNER_R}`,
      `A ${INNER_R} ${INNER_R} 0 1 0 ${CX} ${CY - INNER_R}`,
    ].join(" ");
  }
  const sweep = 360 / count;
  const start = -90 + index * sweep;
  const end = start + sweep;
  const large = sweep > 180 ? 1 : 0;
  const p1 = polar(OUTER_R, start);
  const p2 = polar(OUTER_R, end);
  const p3 = polar(INNER_R, end);
  const p4 = polar(INNER_R, start);
  return [
    `M ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`,
    `A ${OUTER_R} ${OUTER_R} 0 ${large} 1 ${p2.x.toFixed(3)} ${p2.y.toFixed(3)}`,
    `L ${p3.x.toFixed(3)} ${p3.y.toFixed(3)}`,
    `A ${INNER_R} ${INNER_R} 0 ${large} 0 ${p4.x.toFixed(3)} ${p4.y.toFixed(3)}`,
    "Z",
  ].join(" ");
}

const slices = computed(() => {
  const items = fanItems.value;
  const n = items.length;
  const sweep = n ? 360 / n : 0;
  return items.map((item, index) => {
    const mid = -90 + index * sweep + sweep / 2;
    const icon = polar(ICON_R, mid);
    return {
      key: item.key,
      item,
      active: item.active,
      selectable: item.selectable,
      path: slicePath(index, n),
      fillRule: n === 1 ? "evenodd" : "nonzero",
      iconStyle: {
        left: `${icon.x}%`,
        top: `${icon.y}%`,
      },
    };
  });
});

function toggle() {
  emit("update:open", !props.open);
}

function close() {
  emit("update:open", false);
}

function onPick(item) {
  if (!item?.selectable) return;
  if (item.kind === "hero") {
    emit("select-hero", item.hero);
    return;
  }
  emit("select-unit", item.row);
}
</script>

<style scoped>
.battle-unit-dial {
  display: none;
}

@media (max-width: 768px) {
  .battle-unit-dial {
    display: block;
  }

  .wheel-overlay {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    padding: calc(3.25rem + env(safe-area-inset-top, 0px)) 1rem
      calc(1.25rem + env(safe-area-inset-bottom, 0px));
    touch-action: none;
  }

  .wheel-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(4, 10, 20, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  .wheel-stage {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: min(90vw, 420px);
    gap: 1.15rem;
  }

  .wheel {
    position: relative;
    width: min(90vw, 68dvh, 360px);
    aspect-ratio: 1 / 1;
  }

  .wheel-svg {
    width: 100%;
    height: 100%;
    display: block;
    filter: drop-shadow(0 0 18px rgba(0, 246, 255, 0.28));
  }

  .wheel-rim {
    fill: none;
    stroke: rgba(0, 246, 255, 0.55);
    stroke-width: 0.7;
  }

  .wheel-inner-ring {
    fill: rgba(8, 12, 24, 0.96);
    stroke: rgba(0, 246, 255, 0.45);
    stroke-width: 0.55;
  }

  .wheel-slice {
    fill: rgba(10, 18, 32, 0.92);
    stroke: rgba(0, 246, 255, 0.28);
    stroke-width: 0.35;
    cursor: pointer;
  }

  .wheel-slice.active {
    fill: rgba(0, 246, 255, 0.22);
    stroke: #00f6ff;
    stroke-width: 0.7;
    filter: drop-shadow(0 0 4px rgba(0, 246, 255, 0.65));
  }

  .wheel-slice.disabled {
    fill: rgba(8, 10, 16, 0.7);
    cursor: not-allowed;
  }

  .slice-icon {
    position: absolute;
    width: 48px;
    height: 48px;
    transform: translate(-50%, -50%);
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #00f6ff;
    font-size: 1.35rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  .slice-icon.active:not(.hero) {
    filter: drop-shadow(0 0 8px rgba(0, 246, 255, 0.55));
  }

  .slice-icon.hero {
    overflow: hidden;
    background: rgba(8, 12, 24, 0.85);
    border: 1px solid rgba(240, 208, 96, 0.45);
  }

  .slice-icon.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .slice-portrait {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .slice-count {
    position: absolute;
    right: -4px;
    bottom: -4px;
    min-width: 1.25rem;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(8, 12, 24, 0.92);
    border: 1px solid rgba(0, 246, 255, 0.45);
    color: #00f6ff;
    font-family: monospace;
    font-size: 0.62rem;
    line-height: 1.25;
  }

  .wheel-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.7rem;
    text-align: center;
  }

  .panel-kicker {
    margin: 0;
    font-family: "Orbitron", sans-serif;
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #7fbfc8;
  }

  .panel-name {
    margin: 0;
    max-width: 100%;
    font-family: "Orbitron", sans-serif;
    font-size: 1.15rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #e7ecf1;
    line-height: 1.2;
    text-shadow: 0 0 16px rgba(0, 246, 255, 0.25);
  }

  .panel-stepper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 0.15rem;
  }

  .step-btn,
  .step-max {
    height: 40px;
    border: 1px solid rgba(0, 246, 255, 0.5);
    border-radius: 10px;
    background: rgba(8, 12, 24, 0.85);
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
    cursor: pointer;
  }

  .step-btn {
    width: 40px;
    padding: 0;
    font-size: 1.25rem;
  }

  .step-max {
    padding: 0 14px;
    font-size: 0.72rem;
    letter-spacing: 0.1em;
  }

  .step-btn:disabled,
  .step-max:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .step-value {
    min-width: 2.4rem;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 246, 255, 0.45);
    border-radius: 10px;
    background: rgba(0, 246, 255, 0.08);
    color: #00f6ff;
    font-family: monospace;
    font-size: 1.15rem;
  }

  .panel-confirm {
    width: 100%;
    max-width: 16rem;
    height: 44px;
    margin-top: 0.15rem;
    border: 1px solid #00f6ff;
    border-radius: 999px;
    background: rgba(0, 246, 255, 0.14);
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
    font-size: 0.78rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 0 16px rgba(0, 246, 255, 0.28);
  }

  .wheel-close {
    position: absolute;
    top: calc(1rem + env(safe-area-inset-top, 0px));
    right: 1rem;
    z-index: 2;
    width: 36px;
    height: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 246, 255, 0.45);
    border-radius: 50%;
    background: rgba(8, 12, 24, 0.9);
    color: #00f6ff;
    cursor: pointer;
  }

  .dial-pill {
    position: fixed;
    left: 50%;
    bottom: calc(80px + 50px + 12px + env(safe-area-inset-bottom, 0px));
    transform: translateX(-50%);
    z-index: 110;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    max-width: min(320px, calc(100vw - 96px));
    height: 44px;
    padding: 0 0.85rem;
    background: rgba(10, 15, 30, 0.95);
    border: 1px solid #00f6ff;
    border-radius: 999px;
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    cursor: pointer;
    box-shadow: 0 0 14px rgba(0, 246, 255, 0.4);
  }

  .battle-unit-dial.open .dial-pill {
    visibility: hidden;
    pointer-events: none;
  }

  .dial-pill.active {
    background: rgba(0, 246, 255, 0.18);
  }

  .pill-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: rgba(0, 246, 255, 0.12);
  }

  .pill-portrait {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pill-copy {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: none;
    letter-spacing: 0.02em;
    color: #e7ecf1;
  }

  .pill-batch {
    margin-left: 0.35rem;
    color: #00f6ff;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.64rem;
  }

  .pill-caret {
    flex-shrink: 0;
    opacity: 0.8;
  }
}
</style>
